import type { TemplateSet } from '@sosocio/creator-types/app';
import type {
	PageModel, PageObjectModel,
} from '@sosocio/creator-types/project';
import {
	clonePageTemplateLayout, getPageTemplateLayout,
} from './page-template-layout';

/**
 * Captures the slots owned by a page before replacing theme data or changing
 * page geometry. Every linked photo must resolve by slot identity; object
 * frames never stand in for missing layout data.
 *
 * @param pageModel Page whose active slot ownership is being retained.
 * @param objectModels Current page objects, including their persisted slot ids.
 * @param positions Slots resolved from the page's current theme and set.
 * @returns Detached complete layout, or undefined when ownership cannot be verified.
 */
export function capturePageTemplateLayout(
	pageModel: PageModel,
	objectModels: readonly PageObjectModel[],
	positions: TemplateSet['positions'],
): TemplateSet | undefined {
	const retainedLayout = getPageTemplateLayout(
		pageModel,
		pageModel.templateSetId ?? undefined,
	);

	if (!pageModel.template) {
		return undefined;
	}

	const linkedPhotos = objectModels.filter((objectModel) => (
		objectModel.type === 'photo'
		&& objectModel.photoid
		&& objectModel.templatestateid !== null
		&& typeof objectModel.templatestateid !== 'undefined'
		&& !objectModel.decorative
		&& objectModel.z_axis < 900
	));

	if (!linkedPhotos.length) {
		return retainedLayout ? clonePageTemplateLayout(retainedLayout) : undefined;
	}

	const activePositions = retainedLayout?.positions ?? positions;

	if (!linkedPhotos.every((objectModel) => activePositions.some((position) => (
		position.type === 'photo'
		&& String(position.id) === String(objectModel.templatestateid)
	)))) {
		return undefined;
	}

	if (retainedLayout) {
		return clonePageTemplateLayout(retainedLayout);
	}

	return clonePageTemplateLayout({
		id: pageModel.templateSetId ?? positions.map((position) => position.id).join('_'),
		templateId: pageModel.template,
		positions,
	});
}
